import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { CheckCircle, FileText } from 'lucide-react'
import toast from 'react-hot-toast'
import api from '../services/api'

export default function PreConsultFormPage() {
    const { token } = useParams()
    const [profile, setProfile] = useState({})
    const navigate = useNavigate()
    const [loading, setLoading] = useState(true)
    const [submitting, setSubmitting] = useState(false)
    const[error, setError] = useState(null)
    const [success, setSuccess] = useState(false)

    const[formConfig, setFormConfig] = useState(null)
    const [fields, setFields] = useState([])
    const [answers, setAnswers] = useState({}) // Stockera {"q_123": "Fatigue", "sys_weight": "68"}

    // AUTO-SAVE : Restauration
    useEffect(() => {
        const savedAnswers = localStorage.getItem(`bellablue_answers_${token}`)
        if (savedAnswers) {
            try { setAnswers(JSON.parse(savedAnswers)) } catch(e){}
        }
    }, [token])

    // AUTO-SAVE : Sauvegarde à chaque frappe, debounce 500ms
    useEffect(() => {
        if (!Object.keys(answers).length) return

        const timer = setTimeout(() => {
            localStorage.setItem(
            `bellablue_answers_${token}`,
            JSON.stringify(answers)
            )
        }, 500)

        return () => clearTimeout(timer)
    }, [answers, token])


    useEffect(() => {
        api.get(`/api/forms/public/${token}`)

            .then(res => {
                if (res.data.status === 'completed') {
                    setSuccess(true)
                } else {
                    setFormConfig(res.data)
                    try {
                        setFields(JSON.parse(res.data.fields_json))
                    } catch(e) { setError("Erreur de lecture du questionnaire.") }
                }
                setLoading(false)
            })
            .catch(err => {
                setError("Lien invalide ou expiré.")
                setLoading(false)
            })

        api.get('/api/workspaces/public-profile').then(res => setProfile(res.data)).catch(() => {})
    }, [token])

    const handleTextChange = (id, value) => {
        setAnswers(prev => ({ ...prev, [id]: value }))
    }

    const handleCheckboxChange = (id, option, isChecked) => {
        setAnswers(prev => {
            const currentArray = prev[id] || []
            if (isChecked) return { ...prev,[id]: [...currentArray, option] }
            return { ...prev, [id]: currentArray.filter(o => o !== option) }
        })
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        setSubmitting(true)
        try {
            await api.post(`/api/forms/public/${token}`, { answers })
            setSuccess(true)
            localStorage.removeItem(`bellablue_answers_${token}`)
            toast.success("Dossier transmis avec succès !")
        } catch (error) {

            toast.error(error.response?.data?.detail || "Erreur d'envoi.")
        } finally {
            setSubmitting(false)
        }
    }

    if (loading) return <div className="flex justify-center items-center py-32"><div className="spinner" /></div>
    if (error) return <div className="text-center py-32 text-red-500 font-bold">{error}</div>

    if (success) {
        return (
            <div className="max-w-2xl mx-auto px-4 py-16 text-center animate-in zoom-in-95">
                <div className="bg-white rounded-3xl p-10 shadow-xl border border-sage-100">
                    <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-6" />
                    <h1 className="text-3xl font-serif text-sage-800 mb-4">Merci !</h1>
                    <p className="text-sage-600 text-lg">Vos réponses ont bien été transmises.</p>
                    <button onClick={() => navigate('/')} className="btn btn-primary mt-8">Retour à l'accueil</button>
                </div>
            </div>
        )
    }

    return (
        <div className="max-w-3xl mx-auto px-4 py-12">
            <div className="text-center mb-10">
                <div className="w-16 h-16 bg-sage-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FileText className="w-8 h-8 text-sage-600" />
                </div>
                <h1 className="text-3xl md:text-4xl font-serif text-sage-800 mb-4">
                    {formConfig?.template_name}
                </h1>
                <p className="text-sage-600 font-medium">Bonjour {formConfig?.patient_first_name},</p>
                {formConfig?.template_description && (
                    <p className="text-sage-600 mt-2">{formConfig.template_description}</p>
                )}
                <p className="text-xs text-gray-400 mt-4">🔒 Données soumises au secret médical.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                
                {fields.map((field) => {
                    const optionsArray = field.options ? field.options.split(',').map(o => o.trim()) :[]
                    
                    return (
                        <div key={field.id} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 transition-all focus-within:ring-2 focus-within:ring-sage-200">
                            <label className="block text-gray-800 font-bold mb-4 text-lg">
                                {field.label} {field.required && <span className="text-red-500">*</span>}
                            </label>

                            {/* TEXTE COURT */}
                            {field.type === 'text' && (
                                <input 
                                    type="text" 
                                    required={field.required}
                                    value={answers[field.id] || ''}
                                    onChange={(e) => handleTextChange(field.id, e.target.value)}
                                    className="w-full bg-gray-50 border-none focus:ring-sage-400 rounded-xl py-3"
                                    placeholder="Votre réponse..."
                                />
                            )}

                            {/* TEXTE LONG */}
                            {field.type === 'textarea' && (
                                <textarea 
                                    required={field.required}
                                    rows={3}
                                    value={answers[field.id] || ''}
                                    onChange={(e) => handleTextChange(field.id, e.target.value)}
                                    className="w-full bg-gray-50 border-none focus:ring-sage-400 rounded-xl py-3 resize-none"
                                    placeholder="Détaillez votre réponse..."
                                />
                            )}

                            {/* NOMBRE & CHAMPS SYSTEMES */}
                            {(field.type === 'number' || field.type.startsWith('sys_')) && (
                                <input 
                                    type="number" 
                                    step="0.1"
                                    required={field.required}
                                    value={answers[field.id] || ''}
                                    onChange={(e) => handleTextChange(field.id, e.target.value)}
                                    className="w-full md:w-1/2 bg-gray-50 border-none focus:ring-sage-400 rounded-xl py-3"
                                    placeholder={field.type === 'sys_weight' ? 'Poids en kg' : field.type === 'sys_height' ? 'Taille en cm' : '0'}
                                />
                            )}

                            {/* CHOIX UNIQUE (RADIO) */}
                            {field.type === 'radio' && (
                                <div className="space-y-3">
                                    {optionsArray.map((opt, i) => (
                                        <label key={i} className="flex items-center gap-3 p-3 rounded-xl border border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors">
                                            <input 
                                                type="radio" 
                                                name={field.id}
                                                required={field.required}
                                                value={opt}
                                                checked={answers[field.id] === opt}
                                                onChange={(e) => handleTextChange(field.id, e.target.value)}
                                                className="w-5 h-5 text-sage-600 focus:ring-sage-500 border-gray-300"
                                            />
                                            <span className="text-gray-700">{opt}</span>
                                        </label>
                                    ))}
                                </div>
                            )}

                            {/* CHOIX MULTIPLES (CHECKBOX) */}
                            {field.type === 'checkbox' && (
                                <div className="space-y-3">
                                    {optionsArray.map((opt, i) => {
                                        const isChecked = (answers[field.id] ||[]).includes(opt)
                                        return (
                                            <label key={i} className="flex items-center gap-3 p-3 rounded-xl border border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors">
                                                <input 
                                                    type="checkbox" 
                                                    checked={isChecked}
                                                    onChange={(e) => handleCheckboxChange(field.id, opt, e.target.checked)}
                                                    className="w-5 h-5 text-sage-600 rounded focus:ring-sage-500 border-gray-300"
                                                />
                                                <span className="text-gray-700">{opt}</span>
                                            </label>
                                        )
                                    })}
                                </div>
                            )}
                        </div>
                    )
                })}

                <div className="text-center pt-8">
                    <button type="submit" disabled={submitting} className="btn btn-primary px-12 py-4 text-lg shadow-xl hover:scale-105 transition-transform w-full md:w-auto">
                        {submitting ? <div className="spinner border-white w-6 h-6 mx-auto" /> : "Envoyer mes réponses"}
                    </button>
                    <p className="text-xs text-gray-400 mt-4">En validant ce formulaire, vous acceptez que ces données soient traitées par {profile?.cabinet_name || 'votre cabinet'} ({profile?.admin_name || 'le praticien'}) pour votre suivi, dans le respect du secret médical.</p>
                </div>
            </form>
        </div>
    )
}